// 面板通話畫面（狀態在 panelCall.ts；掛在 AppShell，全 App 範圍）：
//   - 來電：大字 caller_name（沒有 → 面板名稱）、不同時小字面板名稱；reason=sos 標「緊急求救」；［接聽］［拒接］
//     瀏覽器沒互動過不能出聲 → 明顯橫幅「點一下這裡開啟鈴聲」（分頁標題閃爍在 panelCall.ts）
//   - 撥出：「正在打給 ○○…」+［掛斷］；接通中 /「○○ 接了」/「面板已自動接通」
//   - 通話中：對方畫面 + 自己小畫面、［靜音］［掛斷］
//   - 結束：一句原因（4 秒後自己收起）
// 求救自動撥的視訊比求救全螢幕更急（家人接了才看得到面板前的人）→ 蓋在求救畫面上面。
import { useEffect, useRef, useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';
import { useFocusTrap } from '../../lib/useFocusTrap';
import { panelCallSubtitle, panelCallTitle } from './panelCallModel';
import { panelCall } from './panelCall';
import './panelCall.css';

/** AppShell 掛載（= 已登入）時開始聽來電，卸載時停 */
export function usePanelCalls(): void {
  useEffect(() => panelCall.start(), []);
}

function Video({ stream, muted, className, testId }: { stream: MediaStream | null; muted: boolean; className: string; testId: string }) {
  const ref = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    const v = ref.current;
    if (!v) return;
    if (v.srcObject !== stream) v.srcObject = stream;
    if (stream) void v.play?.()?.catch?.(() => {});
  }, [stream]);
  return <video ref={ref} className={className} data-testid={testId} autoPlay playsInline muted={muted} />;
}

export function PanelCallOverlay() {
  const { t } = useTranslation();
  const st = useSyncExternalStore(panelCall.subscribe, panelCall.get);
  const cardRef = useRef<HTMLDivElement>(null);
  const open = st.phase !== 'idle';
  useFocusTrap(open && st.phase !== 'ended', cardRef);
  if (!open || !st.call) return null;
  const c = st.call;
  const title = panelCallTitle(c);
  const sub = panelCallSubtitle(c);
  const sos = c.reason === 'sos';

  if (st.phase === 'ended') {
    return (
      <div className="pcall-toast" role="status" data-testid="panel-call-ended" data-reason={st.endReason ?? ''}>
        <span>{t(`panelCall.end_${st.endReason ?? 'ended'}`, { name: title })}</span>
        <button className="btn-ghost" onClick={() => panelCall.dismiss()}>{t('common.close')}</button>
      </div>
    );
  }

  const live = st.phase === 'connecting' || st.phase === 'in_call';
  return (
    <div className={`pcall-scrim${sos ? ' pcall-scrim--sos' : ''}`} role="dialog" aria-modal="true" aria-labelledby="pcall-title"
      data-testid="panel-call" data-phase={st.phase}>
      <div className="pcall-card" ref={cardRef}>
        {live && (
          <div className="pcall-media">
            <Video stream={st.remote} muted={false} className="pcall-remote" testId="panel-call-remote" />
            {st.local && <Video stream={st.local} muted className="pcall-local" testId="panel-call-local" />}
          </div>
        )}
        {sos && <div className="pcall-sos" data-testid="panel-call-sos">{t('panelCall.sos')}</div>}
        <h1 id="pcall-title" className="pcall-title">{title}</h1>
        {sub && <div className="pcall-sub" data-testid="panel-call-sub">{sub}</div>}
        <div className="pcall-status" data-testid="panel-call-status">
          {st.phase === 'ringing' && t('panelCall.incoming')}
          {st.phase === 'calling' && t('panelCall.calling', { name: title })}
          {st.phase === 'connecting' && (st.auto ? t('panelCall.autoAnswered') : st.answeredBy ? t('panelCall.answeredBy', { name: st.answeredBy }) : t('panelCall.connecting'))}
          {st.phase === 'in_call' && (st.auto ? t('panelCall.autoAnswered') : t('panelCall.inCall'))}
        </div>
        {st.phase === 'ringing' && !st.canRing && (
          // 瀏覽器規定互動過才能出聲：點這個橫幅本身就算互動（panelCall 聽 pointerdown）
          <button className="pcall-sound-banner" data-testid="panel-call-enable-sound">{t('panelCall.enableSound')}</button>
        )}
        <div className="pcall-actions">
          {st.phase === 'ringing' ? (
            <>
              <button className="pcall-btn pcall-btn--answer" disabled={st.busy} onClick={() => void panelCall.answer()} data-autofocus data-testid="panel-call-answer">
                {t('panelCall.answer')}
              </button>
              <button className="pcall-btn pcall-btn--hangup" disabled={st.busy} onClick={() => void panelCall.decline()} data-testid="panel-call-decline">
                {t('panelCall.decline')}
              </button>
            </>
          ) : (
            <>
              {live && (
                <button className="pcall-btn" onClick={() => panelCall.toggleMute()} aria-pressed={st.muted} data-testid="panel-call-mute">
                  {st.muted ? t('panelCall.unmute') : t('panelCall.mute')}
                </button>
              )}
              <button className="pcall-btn pcall-btn--hangup" onClick={() => void panelCall.hangup()} data-autofocus data-testid="panel-call-hangup">
                {t('panelCall.hangup')}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
